import { DestroyRef, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  filter,
  map,
  mergeMap,
  Observable,
  of,
  race,
  ReplaySubject,
  switchMap,
  take,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs';
import { RoleConfig, StaticRole, User, UserRole, UserRoles } from '@markant-platform/shared/user/model';
import { UserService } from './user.service';
import { AuthService } from '@auth0/auth0-angular';
import { HttpClient } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AppConfigService } from '@markant-platform/shared/data-access';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class AuthenticatedUserService {
  userId?: string;
  private organizationId?: string;
  private readonly userRoles: BehaviorSubject<UserRoles> = new BehaviorSubject({
    userId: this.userId ?? '',
    roles: [],
  } as UserRoles);
  private readonly authorizerApiUrl: string = AppConfigService.appConfiguration.apiUrl['authorizerService'];

  private readonly rolesInitialized$: ReplaySubject<boolean> = new ReplaySubject(1);
  private readonly user$: ReplaySubject<User> = new ReplaySubject(1);
  private readonly userError$: ReplaySubject<Error | undefined> = new ReplaySubject(1);
  private readonly organizationId$: ReplaySubject<string> = new ReplaySubject(1);

  public readonly WRITE_MFA_SCOPE = 'write:mfa';

  constructor(
    private readonly userService: UserService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly http: HttpClient,
    private readonly destroyRef: DestroyRef
  ) {
    //init user
    this.fetchUser()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => this.user$.next(user));
    // register user update
    this.userService.updatedUser$
      .pipe(
        withLatestFrom(this.user$),
        filter(([updatedUser, currentUser]) => updatedUser.id === currentUser.id),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([updatedUser, currentUser]) =>
        this.user$.next({
          ...currentUser,
          ...updatedUser,
        } as User)
      );

    this.getDataFromUserToken()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(token => {
        this.userId = token.userId;
        this.organizationId = token.organizationId;
        this.organizationId$.next(token.organizationId || '');
        this.updateUserRoles();
      });
  }

  getPlatformUserId(): Observable<string> {
    return this.authService.getAccessTokenSilently().pipe(
      map(token => {
        return new JwtHelperService().decodeToken(token).platform_user_id;
      })
    );
  }

  getSubject(): Observable<string> {
    return this.authService.getAccessTokenSilently().pipe(
      map(token => {
        return new JwtHelperService().decodeToken(token).sub;
      })
    );
  }

  private getDataFromUserToken(): Observable<{ userId?: string; organizationId?: string }> {
    return this.authService.getAccessTokenSilently().pipe(
      map(tokenString => {
        const token: { platform_user_id?: string; organization_id?: string } | null =
          new JwtHelperService().decodeToken(tokenString);
        return { userId: token?.platform_user_id, organizationId: token?.organization_id };
      })
    );
  }

  getUser(): Observable<User> {
    return race(
      this.user$,
      this.userError$.pipe(switchMap(err => (err === undefined ? EMPTY : throwError(() => err))))
    ).pipe(take(1));
  }

  private fetchUser(): Observable<User> {
    return this.getPlatformUserId().pipe(
      switchMap(platformUserId => {
        return this.userService.getUser(platformUserId);
      }),
      catchError(err => {
        this.userError$.next(err);
        return EMPTY;
      }),
      tap(() => this.userError$.next(undefined))
    );
  }

  getMfa(): Observable<boolean> {
    return this.authService.getAccessTokenSilently().pipe(
      map(token => {
        return !!new JwtHelperService().decodeToken(token).selectedMFA;
      })
    );
  }

  getScopes(): Observable<string[]> {
    return this.authService.getAccessTokenSilently().pipe(
      map(token => {
        return new JwtHelperService().decodeToken(token).scope?.split(' ');
      })
    );
  }

  getOrganizationId(): Observable<string> {
    return this.organizationId$.pipe(take(1));
  }

  getUserRoles(): Observable<UserRole[]> {
    return this.rolesInitialized$.pipe(
      switchMap(_ => this.userRoles),
      map(({ roles }) => roles ?? [])
    );
  }

  updateUserRoles(): void {
    if (this.userId) {
      this.http
        .get<UserRoles>(`${this.authorizerApiUrl}/user/${this.userId}/role-assignment`)
        .pipe(
          map(userWithRoles => {
            const isPlatformAdmin = userWithRoles.roles.some(({ name }) => name === StaticRole.PLATFORM_ADMIN);

            if (!isPlatformAdmin) {
              userWithRoles.roles = userWithRoles.roles.filter(
                ({ organizationId }) => organizationId === this.organizationId || !organizationId
              );
            }

            return userWithRoles;
          }),
          catchError(() => {
            this.rolesInitialized$.next(false);
            return EMPTY;
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(userWithRoles => {
          this.userRoles.next(userWithRoles);
          this.rolesInitialized$.next(true);
        });
    } else {
      this.userRoles.next({ userId: '', roles: [] });
    }
  }

  getUserRolesForCartridges(userId: string, organizationId?: string): Observable<UserRoles> {
    const organizationIdParam = !organizationId ? '' : `&organization-id=${organizationId}`;
    return this.http.get<UserRoles>(
      `${this.authorizerApiUrl}/user/${userId}/role-assignment?role-source=cartridges${organizationIdParam}`
    );
  }

  /**
   * Finds the matching RoleConfig for the current route and role from the provided array of roleConfigs.
   * First, the method checks if a roleConfig has a routeIdentifier RegEx that matches the current route.
   * Second, it checks if the currently singed in user has the user role required to access this route.
   *
   * The matching role config is returned.
   *
   * @param roleConfigs
   */
  getMatchingConfigForCurrentRouteAndRole(roleConfigs: RoleConfig[]): Observable<RoleConfig> {
    return this.getUserRoles().pipe(
      take(1),
      map(roles => roles.map(({ name }) => name as StaticRole)),
      mergeMap(roles => {
        const matchingRoleConfig = roleConfigs.find(r => r.routeIdentifier.test(this.router.url));
        if (matchingRoleConfig && roles.some(staticRole => staticRole === matchingRoleConfig.role)) {
          return of(matchingRoleConfig);
        }
        return throwError(() => 'User is not authorized!');
      })
    );
  }
}
